// frontend/src/api.js
import { Preferences } from "@capacitor/preferences"; // ✅ added

// --- Choose API base URL ---
// Priority:
// 1) REACT_APP_API_BASE (from .env)
// 2) localStorage 'apiBase' (quick override; handy on device)
// 3) default production URL
const DEFAULT_PROD = "https://sports-app-backend-zvol.onrender.com";

function normalizeUrl(u) {
  return (u || "").trim().replace(/\/+$/, "");
}

function getBaseURL() {
  const env = normalizeUrl(process.env.REACT_APP_API_BASE);
  if (env) return env;
  const ls = normalizeUrl(localStorage.getItem("apiBase"));
  if (ls) return ls;
  return DEFAULT_PROD;
}

export const BASE_URL = getBaseURL();

// Optional: simple timeout to avoid hanging requests on flaky mobile networks
const FETCH_TIMEOUT_MS = 15000;
function fetchWithTimeout(url, options = {}) {
  return Promise.race([
    fetch(url, options),
    new Promise((_, reject) =>
      setTimeout(() => reject(new Error("Network timeout")), FETCH_TIMEOUT_MS)
    ),
  ]);
}

// ✅ helper to read token securely (Preferences first, localStorage fallback)
async function getToken() {
  try {
    const { value } = await Preferences.get({ key: "authToken" });
    return (
      value ||
      localStorage.getItem("token") ||
      localStorage.getItem("authToken") ||
      localStorage.getItem("coachToken")
    );
  } catch {
    return (
      localStorage.getItem("token") ||
      localStorage.getItem("authToken") ||
      localStorage.getItem("coachToken")
    );
  }
}

export async function apiFetch(path, options = {}) {
  const token = await getToken(); // ✅ use secure helper
  const skipAuthRedirect =
    options.skipAuthRedirect === true || String(path || "").startsWith("/auth/login");

  const headers = {
    "Content-Type": "application/json",
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...(options.headers || {}),
  };

  // Retry logic for backend cold starts (Render free tier spins down when inactive)
  const maxRetries = options.maxRetries !== undefined ? options.maxRetries : 3;
  const retryDelay = options.retryDelay || 1000; // Start with 1 second

  let lastError;
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      const res = await fetchWithTimeout(`${BASE_URL}${path}`, {
        ...options,
        headers,
      });

      const text = await res.text();
      let data;
      try {
        data = text ? JSON.parse(text) : null;
      } catch {
        data = text;
      }

      if (!res.ok) {
        // Session expired: clear token + send to login
        if (res.status === 401) {
          if (!skipAuthRedirect) {
            try {
              await Preferences.remove({ key: "authToken" });
            } catch {}
            localStorage.removeItem("token");
            localStorage.removeItem("authToken");
            localStorage.removeItem("coachToken");
            // Optional user message could be added here (toast), but redirect quickly
            window.location.href = "/login";
            const err = new Error("Session expired. Please log in again.");
            err.status = res.status;
            err.data = data;
            throw err;
          }
        }

        // Retry on 500 errors (server warming up) or 503 (service unavailable)
        if ((res.status === 500 || res.status === 503) && attempt < maxRetries) {
          const delay = retryDelay * Math.pow(2, attempt); // Exponential backoff
          console.log(`Server error ${res.status}, retrying in ${delay}ms... (attempt ${attempt + 1}/${maxRetries})`);
          await new Promise(resolve => setTimeout(resolve, delay));
          continue;
        }

        const msg =
          (data && typeof data === "object" && data.error)
            ? data.error
            : (typeof data === "string" && data) || `HTTP ${res.status}`;
        const err = new Error(msg);
        err.status = res.status;
        err.data = data;
        throw err;
      }
      return data;
    } catch (err) {
      lastError = err;

      // Retry on network errors or timeouts, but not on auth errors (401, 403)
      if (err.status === 401 || err.status === 403) {
        throw err; // Don't retry auth failures
      }

      if (attempt < maxRetries) {
        const delay = retryDelay * Math.pow(2, attempt);
        console.log(`Network error, retrying in ${delay}ms... (attempt ${attempt + 1}/${maxRetries})`, err.message);
        await new Promise(resolve => setTimeout(resolve, delay));
        continue;
      }
    }
  }

  throw lastError;
}

// ---- Convenience APIs ----
export const PracticeAPI = {
  listRange(from, to, team = null) {
    const params = new URLSearchParams({ from, to });
    if (team) params.append("team", team);
    return apiFetch(`/practices/range?${params.toString()}`);
  },
  create(body) {
    return apiFetch(`/practices`, {
      method: "POST",
      body: JSON.stringify(body),
    });
  },
  update(id, body) {
    return apiFetch(`/practices/${id}`, {
      method: "PATCH",
      body: JSON.stringify(body),
    });
  },
  remove(id) {
    return apiFetch(`/practices/${id}`, { method: "DELETE" });
  },
};
